'use client';

import { c as _c } from "react/compiler-runtime";
import { type ElementType, useState } from 'react';
import MUIIconButton, { IconButtonProps as MUIIconButtonProps, IconButtonTypeMap as MUIIconButtonTypeMap } from '@mui/material/IconButton';
import { alpha, styled } from '../styles';
import Tooltip from '../Tooltip';
type Variant = 'destructive' | 'default' | 'primary';
const IconButtonStyled = styled(MUIIconButton, {
  shouldForwardProp: /* istanbul ignore next */(prop: string) => prop !== 'variant'
})<{
  variant?: Variant;
}>(({
  theme: {
    palette,
    shadows
  },
  variant
}) => {
  const HOVER_MEDIA_QUERY = '@media (hover: hover) and (pointer: fine)';
  const VARIANTS: Record<string, object> = {
    destructive: {
      '&, &.Mui-disabled': {
        color: palette.interaction.danger
      },
      '& .MuiSvgIcon-root': {
        color: palette.interaction.danger
      },
      [HOVER_MEDIA_QUERY]: {
        '&:hover': {
          '&:not(:active)': {
            backgroundColor: alpha(palette.interaction.danger, 0.16)
          }
        }
      },
      '&.Mui-focusVisible': {
        backgroundColor: alpha(palette.interaction.danger, 0.32),
        '.MuiSvgIcon-root': {
          color: palette.interaction.danger
        }
      }
    },
    primary: {
      color: palette.interaction.onEnabled,
      backgroundColor: palette.interaction.enabled,
      [HOVER_MEDIA_QUERY]: {
        '&:hover': {
          boxShadow: shadows.containedButtonRaised,
          backgroundColor: alpha(palette.interaction.enabled, 0.64)
        }
      },
      '&.Mui-focusVisible': {
        backgroundColor: alpha(palette.interaction.enabled, 0.8)
      },
      '&.Mui-disabled': {
        backgroundColor: palette.interaction.disabled,
        '.MuiSvgIcon-root': {
          color: palette.icon.onMainHighlighted
        }
      }
    }
  };
  return {
    color: palette.icon.onMain,
    '&.Mui-disabled .MuiSvgIcon-root': {
      color: palette.icon.disabled
    },
    [HOVER_MEDIA_QUERY]: {
      '&:hover': {
        backgroundColor: alpha(palette.interaction.hover, 0.16),
        '.MuiSvgIcon-root': {
          color: palette.icon.onMainHighlighted
        }
      }
    },
    '&.Mui-focusVisible': {
      backgroundColor: alpha(palette.interaction.hover, 0.32),
      '.MuiSvgIcon-root': {
        color: palette.icon.onMainHighlighted
      }
    },
    ...(variant ? VARIANTS[variant] : /* istanbul ignore next */{})
  };
});
function IconButton(t0) {
  const $ = _c(20);
  let canOpenTooltip;
  let children;
  let disabled;
  let otherProps;
  let t1;
  let t2;
  let tooltip;
  if ($[0] !== t0) {
    ({
      tooltip,
      children,
      canOpenTooltip,
      disabled,
      hasTouchTooltip: t1,
      variant: t2,
      ...otherProps
    } = t0);
    $[0] = t0;
    $[1] = canOpenTooltip;
    $[2] = children;
    $[3] = disabled;
    $[4] = otherProps;
    $[5] = t1;
    $[6] = t2;
    $[7] = tooltip;
  } else {
    canOpenTooltip = $[1];
    children = $[2];
    disabled = $[3];
    otherProps = $[4];
    t1 = $[5];
    t2 = $[6];
    tooltip = $[7];
  }
  const hasTouchTooltip = t1 === undefined ? true : t1;
  const variant = t2 === undefined ? "default" : t2;
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  let t3;
  if ($[8] !== children || $[9] !== disabled || $[10] !== otherProps || $[11] !== variant) {
    t3 = <IconButtonStyled {...otherProps} disabled={disabled} variant={variant}>{children}</IconButtonStyled>;
    $[8] = children;
    $[9] = disabled;
    $[10] = otherProps;
    $[11] = variant;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const icon = t3;
  let t4;
  if ($[13] !== canOpenTooltip || $[14] !== disabled || $[15] !== hasTouchTooltip || $[16] !== icon || $[17] !== isTooltipOpen || $[18] !== tooltip) {
    t4 = disabled ? icon : <Tooltip title={tooltip} disableTouchListener={!hasTouchTooltip} onClose={canOpenTooltip ? () => setIsTooltipOpen(false) : undefined} onOpen={canOpenTooltip ? () => {
      if (canOpenTooltip()) {
        setIsTooltipOpen(true);
      }
    } : undefined} open={canOpenTooltip ? isTooltipOpen : undefined}><span>{icon}</span></Tooltip>;
    $[13] = canOpenTooltip;
    $[14] = disabled;
    $[15] = hasTouchTooltip;
    $[16] = icon;
    $[17] = isTooltipOpen;
    $[18] = tooltip;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  return t4;
}
export type Props<D extends ElementType = MUIIconButtonTypeMap['defaultComponent']> = MUIIconButtonProps<D, {
  component?: D;
}> & {
  canOpenTooltip?: () => boolean;
  hasTouchTooltip?: boolean;
  tooltip: string;
  variant?: Variant;
};

// Use this fixed button-only adapter when passing IconButton to styled(...).
// The polymorphic generic IconButton type does not compose cleanly with styled(),
// see PasswordField's VisibilityToggleButton for the intended usage pattern.
export type FixedIconButtonProps = Props<'button'>;
export const FixedIconButton: (props: FixedIconButtonProps) => ReturnType<typeof IconButton> = IconButton;
export default IconButton;